import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useStepSave, type BaseStepOverrides } from './useStepSaveFactory';
import { useAutoSave } from './useAutoSave';

interface UseTagStepSaveProps {
	tagStep: Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep | null;
	stepId: string;
	stepType: Api.AutomationStepType;
	templateId: string | undefined;
	tagName: string;
	autoSave: ReturnType<typeof useAutoSave>;
}

interface UseTagStepSaveOverrides extends BaseStepOverrides {
	newTagName?: string;
	newIncludeMeetingGuests?: boolean;
}

interface UseTagStepSaveResult {
	save: (overrides?: UseTagStepSaveOverrides) => Promise<void>;
}

/**
 * Save hook for TagStepRenderer.
 * Handles saving tag step changes via the auto-save system.
 */
export function useTagStepSave({
	tagStep,
	stepId,
	stepType,
	templateId,
	tagName,
	autoSave,
}: UseTagStepSaveProps): UseTagStepSaveResult {
	const buildUpdatedStep = useCallback(
		(
			step: Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep | null,
			overrides: UseTagStepSaveOverrides
		): Partial<Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep> => ({
			...step,
			tagName: overrides.newTagName ?? tagName,
			...(overrides.newIncludeMeetingGuests !== undefined && {
				includeMeetingGuests: overrides.newIncludeMeetingGuests,
			}),
		}),
		[tagName]
	);

	return useStepSave<Api.IAddTagAutomationStep | Api.IRemoveTagAutomationStep, UseTagStepSaveOverrides>({
		step: tagStep,
		stepId,
		stepType,
		templateId,
		autoSave,
		buildUpdatedStep,
	});
}
